/**
 * The Chinese an article writes about, redrawn in the writing a reader chose.
 *
 * #335 gave a reader the switch and the 例句 followed it, because a sentence
 * page holds every writing of its sentence and the stylesheet draws one. The
 * Chinese in the prose around those examples did not: `倍` in the sentence
 * explaining 倍 is characters typed into a line of English, and nothing had
 * ever converted them. So a reader in Taipei read the explanation in 简体 and
 * the examples under it in 繁體, a paragraph apart.
 *
 * **The conversion is precomputed and the browser only looks it up.** An
 * accurate 简→繁 conversion needs the dictionary, because 发 is 發 or 髮 and
 * only the reading of the word it sits in says which, and the dictionary and
 * the script tables together are 482 KB. The site's whole prose vocabulary is
 * 330 characters, so `cli/writings.mts` converts all of it at build time and
 * the answer rides in this bundle: 1,492 bytes of JSON, which with the code
 * below took the bundle from 964 brotli-compressed bytes to 2,542.
 *
 * **In the bundle rather than in the page**, which is the trade the copy
 * buttons above it are here for: the bundle is one hashed file every page
 * already asks for, so a table carried in it is fetched once for a whole visit,
 * where a table written into each article is 1,127 gzipped bytes on every
 * article a reader opens. See `WritingForms` for the table itself and
 * `docs/user-features.md` for the rest.
 *
 * **A reader on 简体 runs none of this**, which is what the first line is for.
 * They are also who the page is drawn for: nothing here changes the markup
 * Hugo published, so the `<title>`, the meta description and what a crawler
 * reads are the same page they always were, and a reader whose script never
 * ran gets 简体 exactly as before.
 *
 * **What it will not touch.** A 例句 block carries its own writings and #335
 * draws them. A `code` or `pre` block is a sample of something rather than
 * prose about Chinese. Everything else inside the article is converted,
 * headings included.
 */

import writings from "./writings.json" with { type: "json" };
import { writingTags } from "../content/chinese/language-tags.js";
import {
  WritingForms,
  type WritingFormsData,
} from "../content/chinese/writing-forms.js";
import { chosenSettings } from "./reader-choice.js";

/**
 * The table, as `writings.json` beside this file holds it.
 *
 * Cast rather than validated. It is committed derived content that
 * `cli/writings.mts` wrote and the pull request diffs, so it is as much a part
 * of this bundle as the code around it, and a browser that has this file has
 * the table that was built with it.
 */
const table = writings as WritingFormsData;

/** The region an article's layout marks as the prose to convert. */
const proseSelector = "[data-cb-prose]";

/** The tag the site writes 简体 under, and the one being replaced. */
const simplified = writingTags["zh-CN"].hanzi;

/**
 * What is left alone inside that region, and everything under it.
 *
 * The 例句 lines name the writing they serve and are drawn by
 * `components/reader-settings.scss`, so converting one would be rewriting a
 * line the page is already choosing between. `code` and `pre` hold samples: a
 * dev-blog post quoting a content file is quoting it, and a sample rewritten
 * under the reader is a sample that no longer says what it was quoted for.
 */
const leftAlone =
  ".liju-block, .liju-heading, [data-cb-writing], code, pre, script, style";

/**
 * Whether an element sits inside something the conversion leaves alone.
 *
 * The nearest match has to be inside the region, and that is not a detail.
 * `<html>` carries `data-cb-writing`, which is where the reader's choice is
 * written and how this module knows there is anything to do. A bare `closest`
 * walks past the article and answers yes for every element on the page.
 */
function isLeftAlone(element: Element, region: Element): boolean {
  const nearest = element.closest(leftAlone);

  return nearest !== null && region.contains(nearest);
}

/** The characters a bracketed reading is written after. */
const anchorPattern = /[\p{Script=Han}…]+$/u;

/** The brackets a reading opens and closes with, either width. */
const openPattern = /^(\s*[(（])/u;
const closePattern = /([)）]\s*)$/u;

/** Whether a writing is one this rewrites anything for. */
function isRedrawn(writing: string): writing is "zh-HK" | "zh-TW" {
  return writing === "zh-TW" || writing === "zh-HK";
}

/** The characters a reading is written beside, where it is written beside any. */
function anchorBefore(gloss: Element): string | undefined {
  const before = gloss.previousSibling;

  return before === null || before.nodeType !== Node.TEXT_NODE
    ? undefined
    : anchorPattern.exec(before.textContent ?? "")?.[0];
}

/**
 * A bracketed reading, re-read under 國語.
 *
 * The brackets and the space before them stay as they were and only what is
 * between them is replaced, so a reading that opens a heading keeps its lack of
 * a separator and one written mid-heading keeps its space. `GlossMarkup` puts
 * both inside the element on purpose; see `.gloss` in
 * `components/reader-settings.scss` for what that separator is doing there.
 */
function reread(gloss: Element, reading: string): void {
  const written = gloss.textContent ?? "";
  const open = openPattern.exec(written)?.[1];
  const close = closePattern.exec(written)?.[1];

  if (open !== undefined && close !== undefined) {
    gloss.innerHTML = open + reading + close;
  }
}

/**
 * Every reading in the region that 國語 reads differently, replaced.
 *
 * Before the characters are converted, because the table is keyed by the 简体
 * the reading was written beside and that is what the page still says at this
 * point.
 */
function rereadGlosses(region: Element, forms: WritingForms): void {
  for (const gloss of region.querySelectorAll(".gloss")) {
    if (isLeftAlone(gloss, region)) {
      continue;
    }

    const anchor = anchorBefore(gloss);
    const reading = anchor === undefined ? undefined : forms.reading(anchor);

    if (reading !== undefined) {
      reread(gloss, reading);
    }
  }
}

/** Every text node in the region that the conversion is allowed to reach. */
function proseTextIn(region: Element): readonly Text[] {
  const walker = region.ownerDocument.createTreeWalker(
    region,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node: Node): number => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return NodeFilter.FILTER_ACCEPT;
        }

        return (node as Element).matches(leftAlone)
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_SKIP;
      },
    },
  );

  const found: Text[] = [];

  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    found.push(node as Text);
  }

  return found;
}

/**
 * The language tag on every element that declared itself 简体, brought up to
 * date.
 *
 * `shortcodes/hanzispan.html` writes `zh-Hans-CN` around a character an article
 * is talking about, and a `lang` naming the script the text is no longer in
 * tells a browser to pick the wrong face for exactly the word that matters.
 *
 * The readings are deliberately left alone. A `pinyinspan` holds a reading
 * somebody wrote into the prose rather than one this can re-read, so retagging
 * it as 國語 would be relabelling 普通话 rather than converting anything. The
 * readings that *are* re-read carry their own tag, put there by `toHtml` when
 * the table was built.
 */
function retagWriting(region: Element, hanzi: string): void {
  for (const element of region.querySelectorAll(`[lang="${simplified}"]`)) {
    if (!isLeftAlone(element, region)) {
      element.setAttribute("lang", hanzi);
    }
  }
}

/**
 * Redraw one article's prose, its headings and the readings beside them.
 *
 * @param page - The document to rewrite, which is the page being read.
 */
export function startArticleWriting(page: Document): void {
  const { writing } = chosenSettings(page);
  const regions = page.querySelectorAll(proseSelector);

  if (!isRedrawn(writing) || regions.length === 0) {
    return;
  }

  const forms = new WritingForms({ data: table });

  for (const region of regions) {
    rereadGlosses(region, forms);

    for (const text of proseTextIn(region)) {
      const written = forms.written(text.data, writing);

      if (written !== text.data) {
        text.data = written;
      }
    }

    retagWriting(region, writingTags[writing].hanzi);
  }
}
